import type { Locale, PhraseRow } from '../../../types/locale';
import { normalizePhrase } from '../../../utils/domTranslator';
import { countries } from '../../countries';
import { dialCodes } from '../../dialCodes';
import { commonPhrases } from './common';
import { homePhrases } from './home';
import { newsPhrases } from './news';
import { experienceArticlePhrases } from './experienceArticles';
import { requestPhrases } from './request';
import { memberPhrases } from './members';
import { categoryPhrases } from './categories';
import { venturistPhrases } from './venturist';

const allPhrases: PhraseRow[] = [
  ...commonPhrases,
  ...homePhrases,
  ...newsPhrases,
  ...experienceArticlePhrases,
  ...requestPhrases,
  ...memberPhrases,
  ...categoryPhrases,
  ...venturistPhrases,
];

const column: Record<Exclude<Locale, 'en'>, 1 | 2 | 3 | 4> = { de: 1, fr: 2, pl: 3, ar: 4 };
const cache = new Map<Locale, Map<string, string>>();

// English phrase → phrase in the chosen language. Country names come from the browser's own region names.
export function phraseMap(locale: Locale): Map<string, string> {
  const cached = cache.get(locale);
  if (cached) return cached;
  const map = new Map<string, string>();
  if (locale !== 'en') {
    addCountryNames(map, locale);
    const col = column[locale];
    for (const row of allPhrases) map.set(normalizePhrase(row[0]), row[col]);
  }
  cache.set(locale, map);
  return map;
}

function addCountryNames(map: Map<string, string>, locale: Locale) {
  if (typeof Intl === 'undefined' || !('DisplayNames' in Intl)) return;
  try {
    const names = new Intl.DisplayNames([locale], { type: 'region' });
    const all = [...countries.map((c) => ({ iso: c.iso, name: c.name })), ...dialCodes.map((d) => ({ iso: d.iso, name: d.country }))];
    for (const { iso, name } of all) {
      const local = names.of(iso);
      if (local) map.set(name, local);
    }
  } catch {
    // Older browsers keep the English country names
  }
}
